import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { useUsers } from '../../../hooks/api/useUsers';
import { useAccountStats } from '../../../hooks/api/useAccountStats';
import { Dayjs } from 'dayjs';
import { ModalDialog } from '../../../components/ModalDialog';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { AlertToast } from '../../../components/AlertToast';

export type AddAccountDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const AddAccountDialog = ({
  open,
  onClose,
  onSuccess,
}: AddAccountDialogProps) => {
  const { accounts: accountList } = useUsers();
  const { createAccount } = useAccountStats();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [contractExpirationDate, setContractExpirationDate] =
    useState<Dayjs | null>(null);
  const [trial, setTrial] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [pathErrorMessage, setPathErrorMessage] = useState('');
  const [error, setError] = useState(false);

  const validatePath = (value: string) => {
    if (/[^a-zA-Z0-9\-_]/.test(value)) {
      return 'Path should not contain special characters';
    }
    if (/^[0-9]/.test(value)) {
      return 'Path should not start with a number';
    }
    if (/-/.test(value) && /^-/.test(value)) {
      return 'Path should not start with a dash';
    }
    if (/-/.test(value) && /-$/.test(value)) {
      return 'Path should not end with a dash';
    }
    if (/--/.test(value)) {
      return 'Path should not contain consecutive dashes';
    }
    if (/[A-Z]/.test(value)) {
      return 'Path should not contain uppercase letters';
    }
    if (/__/.test(value)) {
      return 'Path should not contain consecutive underscores';
    }

    return '';
  };

  const handleSubmit = async () => {
    setNameErrorMessage('');
    setPathErrorMessage('');

    const newNameErrorMessage = !name ? 'Please enter account name' : '';
    const newPathErrorMessage = !path
      ? 'Please enter account path'
      : validatePath(path);

    setNameErrorMessage(newNameErrorMessage);
    setPathErrorMessage(newPathErrorMessage);

    const pathExists = Object.values(accountList).some(
      (account) => account === path
    );

    const nameExists = Object.keys(accountList).some(
      (account) => account === name
    );

    if (nameExists) {
      setNameErrorMessage('Name already exists');
      return;
    }

    if (pathExists) {
      setPathErrorMessage('Path already exists');
      return;
    }

    if (newNameErrorMessage || newPathErrorMessage) {
      return;
    }

    if (name && path) {
      try {
        await createAccount({
          name,
          path,
          contract_expiration_date: contractExpirationDate
            ? contractExpirationDate.format('YYYY-MM-DD')
            : undefined,
          trial,
        });

        onClose();
        setName('');
        setPath('');
        setContractExpirationDate(null);
        setTrial(false);
        setShowToast(true);
        if (onSuccess) {
          onSuccess();
        }
        setTimeout(() => {
          setShowToast(false);
        }, 1600);
      } catch (err: unknown) {
        const error = err as { response?: { data?: { path?: string[] } } };
        const errors = error.response?.data;
        const pathError = errors?.path && errors.path.length > 0;
        if (pathError) {
          setPathErrorMessage('Path already exists');
        }
        console.error('Error creating account:', err);
        setError(true);
        setTimeout(() => {
          setError(false);
        }, 1600);
      }
    }
  };

  const handleClose = () => {
    onClose();
    setName('');
    setPath('');
    setContractExpirationDate(null);
    setTrial(false);
    setNameErrorMessage('');
    setPathErrorMessage('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ModalDialog
        open={open}
        onClose={handleClose}
        title={t('accounts.addAccountDialog.title')}
        subtitle={t('accounts.addAccountDialog.subtext') as string}
        actions={
          <>
            <Button variant="default" onClick={handleClose}>
              {t('accounts.addAccountDialog.cancel')}
            </Button>
            <Button onClick={handleSubmit} variant="primary">
              {t('accounts.addAccountDialog.create')}
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <Box>
            <StyledInputLabel htmlFor="name">
              {t('accounts.editAccountDialog.name', 'Account Name')}{' '}
              <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              required
              id="name"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!nameErrorMessage}
              helperText={nameErrorMessage}
              placeholder={String(t('accounts.addAccountDialog.name'))}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="path">
              {t('accounts.editAccountDialog.path', 'Account Path')}{' '}
              <span aria-hidden="true">*</span>
            </StyledInputLabel>
            <TextField
              required
              id="path"
              type="text"
              fullWidth
              value={path}
              onChange={(e) => setPath(e.target.value)}
              error={!!pathErrorMessage}
              helperText={pathErrorMessage}
              placeholder={String(t('accounts.addAccountDialog.path'))}
            />
          </Box>

          <Box>
            <StyledInputLabel htmlFor="contract_expiration_date">
              {t(
                'accounts.editAccountDialog.contractExpiration',
                'Contract Expiration Date'
              )}
            </StyledInputLabel>
            <DatePicker
              value={contractExpirationDate}
              onChange={(newValue) => setContractExpirationDate(newValue)}
              format="YYYY-MM-DD"
              slotProps={{
                textField: {
                  id: 'contract_expiration_date',
                  fullWidth: true,
                  placeholder: 'YYYY-MM-DD',
                },
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={trial}
                onChange={(e) => setTrial(e.target.checked)}
              />
            }
            label={t('accounts.trialAccount')}
          />
        </Box>
      </ModalDialog>
      {showToast && (
        <AlertToast severity="success">
          {t('accounts.addAccountDialog.success')}
        </AlertToast>
      )}
      {error && (
        <AlertToast severity="error">
          {t('accounts.addAccountDialog.error')}
        </AlertToast>
      )}
    </LocalizationProvider>
  );
};

export default AddAccountDialog;
